import React, { useRef, useEffect } from "react";
import { Box, Typography, Button, Container, useMediaQuery } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

interface Blog {
  _id: string;
  title: string;
  description?: string;
  imageurl: string;
  posturl?: string;
  slug?: string;
  metaDescription?: string;
  sections?: { description: string }[];
}

interface BlogsProps {
  data: { blogs?: Blog[] };
}

const IMAGE_DOMAIN = "https://dprstorage.b-cdn.net/";

gsap.registerPlugin(ScrollTrigger);

const Blogs: React.FC<BlogsProps> = ({ data }) => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:600px)");
  const sectionRef = useRef<HTMLDivElement>(null);

  const blogs = Array.isArray(data.blogs) ? data.blogs : [];

  useEffect(() => {
    const el = sectionRef.current;
    if (el) {
      gsap.fromTo(
        el,
        { opacity: 0, y: 50 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: el,
            start: "top 87%",
            toggleActions: "play none none reverse",
          },
        }
      );
    }
    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, []);

  if (!blogs.length) {
    return (
      <Typography color="#ccc" textAlign="center" mt={4}>
        No blogs found.
      </Typography>
    );
  }

  return (
    <>
      {/* <Helmet>
        <title>Best Apartments in Hyderabad | Kolla Group Blogs</title>
        <meta
          name="description"
          content="Read the latest blogs from Kolla Group about luxury living, sustainability, and upcoming projects."
        />
        <meta
          name="keywords"
          content="Luxury villas in hyderabad, high rise apartments hyderabad, Luxury villas in hyderabad gated communities, best apartments in hyderabad, best villas in hyderabad, ultra luxury apartments in hyderabad, premium apartments in hyderabad, ultra luxury villas in hyderabad, top 10 luxury apartments in hyderabad, top apartments in hyderabad, luxury house in hyderabad, luxury homes in hyderabad, best villa projects in hyderabad"
        />
      </Helmet> */}

      <Box
        ref={sectionRef}
        sx={{
          px: { xs: 0, md: 4 },
          py: { xs: 3, md: 4 },
          background: "#121212",
        }}
      >
        <Container maxWidth={isMobile ? false : "xl"}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 600,
              fontSize: 28,
              background:
                "linear-gradient(90deg, #bf742c, rgb(252, 243, 202), #c58038)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              display: "inline-block",
              fontFamily: "LEMON MILK",
              mb: 4,
            }}
          >
            Blogs
          </Typography>

          {blogs.map((blog, idx) => {
            const description =
              blog.metaDescription ||
              blog.sections?.[0]?.description ||
              blog.description ||
              "";

            const imageUrl = blog.imageurl?.startsWith("http")
              ? blog.imageurl
              : IMAGE_DOMAIN + blog.imageurl;

            // Alternate image/content sides
            const flexDirection = isMobile
              ? "column"
              : idx % 2 === 0
              ? "row"
              : "row-reverse";

            const handleNavigate = () => {
              if (blog.slug) navigate(`/blogs/${blog.slug}`);
            };

            return (
              <Box
                key={blog._id}
                sx={{
                  display: "flex",
                  flexDirection,
                  mb: 4,
                  borderRadius: 3,
                  overflow: "hidden",
                  boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
                  background: "#181818",
                  minHeight: 220,
                }}
              >
                {/* Image Section */}
                <Box
                  sx={{
                    width: { xs: "100%", md: "35%" },
                    height: { xs: 180, md: "auto" },
                    minHeight: { md: 220 },
                    maxHeight: { md: 300 },
                    flexShrink: 0,
                    overflow: "hidden",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "#181818",
                    cursor: "pointer",
                  }}
                  onClick={handleNavigate}
                >
                  <Box
                    component="img"
                    src={imageUrl}
                    alt={blog.title}
                    sx={{
                      width: "100%",
                      height: "90%",
                      objectFit: "cover",
                      objectPosition: "center",
                      background: "#181818",
                      display: "block",
                      m: 2,
                      borderRadius: "10px",
                    }}
                  />
                </Box>

                {/* Content Section */}
                <Box
                  sx={{
                    width: { xs: "100%", md: "65%" },
                    p: { xs: 2, md: 3 },
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-start",
                    background: "#181818",
                  }}
                >
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 600,
                      fontSize: 24,
                      background:
                        "linear-gradient(90deg, #bf742c, rgb(252, 243, 202), #c58038)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      display: "inline-block",
                      fontFamily: "LEMON MILK",
                      mb: 4,
                      cursor: "pointer",
                    }}
                    onClick={handleNavigate}
                  >
                    {blog.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#cfcfcf",
                      mb: 2,
                      fontSize: { xs: "14px", md: "16px" },
                      lineHeight: 1.7,
                    }}
                  >
                    {description}
                  </Typography>

                  {/* Push the button down */}
                  <Box sx={{ flexGrow: 1 }} />

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-start",
                      mt: 2,
                    }}
                  >
                    <Button
                      variant="contained"
                      endIcon={<ArrowForwardIcon />}
                      sx={{
                        alignSelf: "flex-end",
                        background:
                          "linear-gradient(90deg, #9f8054 60%, #bf742c 100%)",
                        color: "#fff",
                        fontWeight: 700,
                        borderRadius: "20px",
                        px: 3,
                        py: 1,
                        textTransform: "uppercase",
                        fontSize: "14px",
                        letterSpacing: 1,
                        boxShadow: "0 4px 16px rgba(159,128,84,0.18)",
                        "&:hover": {
                          background:
                            "linear-gradient(90deg, #bf742c 60%, #9f8054 100%)",
                        },
                      }}
                      onClick={handleNavigate}
                    >
                      Read More
                    </Button>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Container>
      </Box>
    </>
  );
};

export default Blogs;
